import {
  ElementLocalizationUtils,
  generateLocalizationKey,
  getLocalizationKeyInElementData,
} from '@packages/domains/localization/dom/model';
import type {DesignerStores} from '@packages/systems/designer/flux';
import {selectIsOnPrimaryLocale} from '@packages/systems/localization';
import {getDataTypeFromElement} from '@packages/systems/user-components/utils';
import type {ExpressionPath} from '@packages/systems/wfdl';
import {Component, Expressions} from '@packages/systems/wfdl';
import {
  getLiveComponentMap,
  getPluginsFromState,
  selectCurrentComponent,
} from '@packages/utilities/platform-selectors';
import {selectExternalizedLocalizationBindingsEnabled} from './selectors';

export type ImageIdChangedLocalization = {
  imgKey: string;
  srcKey: string;
};

/**
 * Returns a localization key for writing `expressionPath` on `elementId` when
 * the edit should land in DomLocalizationStore; otherwise undefined.
 */
export function selectLocalizationKeyForExpression(
  state: DesignerStores,
  elementId: string,
  expressionPath: ExpressionPath
): string | undefined {
  if (
    !state.DomLocalizationStore ||
    !selectExternalizedLocalizationBindingsEnabled(state.DomLocalizationStore)
  ) {
    return undefined;
  }

  const element = findElement(state, elementId);
  if (!element) return undefined;

  const plugins = getPluginsFromState(state);
  const components = getLiveComponentMap(state.DesignerStore);
  const details =
    ElementLocalizationUtils.getLocalizationDetailsAtExpressionPath({
      element,
      path: expressionPath,
      dataType: getDataTypeFromElement(plugins)(components)(element),
      plugins,
    });
  if (!details) return undefined;

  const isOnPrimaryLocale = selectIsOnPrimaryLocale(state.LocalizationStore);
  if (isOnPrimaryLocale && !details.existingLocalizationKey) {
    return undefined;
  }

  return details.existingLocalizationKey ?? generateLocalizationKey();
}

/**
 * Existing localization key on the path, or a newly generated one when
 * externalized bindings are enabled and the element exists.
 */
export function selectOrCreateLocalizationKeyForExpression(
  state: DesignerStores,
  elementId: string,
  expressionPath: ExpressionPath
): string | undefined {
  if (
    !state.DomLocalizationStore ||
    !selectExternalizedLocalizationBindingsEnabled(state.DomLocalizationStore)
  ) {
    return undefined;
  }

  const element = findElement(state, elementId);
  if (!element) return undefined;

  return (
    getLocalizationKeyInElementData(element, expressionPath) ??
    generateLocalizationKey()
  );
}

/**
 * Localization keys for IMAGE_ID_CHANGED. Undefined when the image edit should
 * write the literal on the element instead of DomLocalizationStore.
 */
export function getImageIdChangedLocalizationKeys(
  state: DesignerStores,
  elementId: string
): ImageIdChangedLocalization | undefined {
  const imgKey = selectLocalizationKeyForExpression(state, elementId, [
    {in: 'Record', at: 'img'},
  ]);
  if (!imgKey) return undefined;

  const srcKey = selectOrCreateLocalizationKeyForExpression(state, elementId, [
    {in: 'Record', at: 'attr'},
    {in: 'Record', at: 'src'},
  ]);
  if (!srcKey) return undefined;

  return {imgKey, srcKey};
}

/**
 * Localization keys for BACKGROUND_VIDEO_CHANGED. Returns null when the edit
 * should write literals on the element instead of DomLocalizationStore.
 *
 * Mirrors Image: gate on video_urls via selectLocalizationKeyForExpression,
 * then selectOrCreate the paired poster key. Returns null unless both keys
 * resolve, so bindings are only installed when the whole pair is localizable.
 */
export function buildBackgroundVideoChangedLocalizationKeys(
  state: DesignerStores,
  elementId: string
): {
  videoUrlsLocalizationKey?: string;
  posterImageUrlLocalizationKey?: string;
} | null {
  const videoUrlsLocalizationKey = selectLocalizationKeyForExpression(
    state,
    elementId,
    [
      {in: 'Record', at: 'bgvideo'},
      {in: 'Record', at: 'video_urls'},
    ]
  );
  if (!videoUrlsLocalizationKey) return null;

  const posterImageUrlLocalizationKey =
    selectOrCreateLocalizationKeyForExpression(state, elementId, [
      {in: 'Record', at: 'bgvideo'},
      {in: 'Record', at: 'poster_image_url'},
    ]);
  if (!posterImageUrlLocalizationKey) return null;

  return {videoUrlsLocalizationKey, posterImageUrlLocalizationKey};
}

function findElement(state: DesignerStores, elementId: string) {
  const currentComponent = selectCurrentComponent(state);
  return Expressions.getElementById(
    Component.getRender(currentComponent),
    elementId
  );
}
